import * as React from "react";
import { useState } from "react";
import { useNavigate } from "react-router";
import { RdPopover } from "../../redesign/table/RdPopover";
import { LinkSelectionDialog } from "../detail/LinkRow";
import {
    kontaktLabel,
    risikoLabel,
    vertragLabel,
    type CascadeResult,
    type KontaktRef,
    type LinkSlots,
    type RisikoRef,
    type VertragRef,
} from "../detail/linkSlots";
import { kontakteGewaehlt, risikenGewaehlt, vertraegeGewaehlt } from "./bezugAuswahl";
import { FoBoBadge } from "../../shared/foBo";
import { FirmaIcon, PersonIcon } from "../../redesign/kvpIcons";
import { IcoPlus } from "./vg3Icons";
import "./vorgangV3.css";

/**
 * Zuweisung Kunde · Risiko · Vertrag als Chip-Reihe mit EINEM „Zuweisung
 * ändern" dahinter (PO 03.09.2026). Vorher trug jeder Slot sein eigenes
 * „ändern" — bei drei Slots dreimal dasselbe Wort in einer Zeile, das die
 * Werte selbst zur Nebensache machte. Das Menü fragt jetzt einmal, WAS
 * geändert werden soll, und öffnet dann die passende Auswahl.
 *
 * Neu gebaut im kvp-rd-Design; die KASKADE (welche Auswahl leert oder füllt
 * welchen anderen Slot) kommt unverändert aus `linkSlots.ts` — reine,
 * getestete Logik, in Altansicht und Erstell-Flow dieselbe. Auch der
 * Auswahl-Dialog wird geteilt.
 */
type Props = {
    slots: LinkSlots;
    canEdit: boolean;
    /** Persistiert den Patch und übernimmt `result.slots` in den Seiten-State. */
    onApply: (result: CascadeResult) => void | Promise<void>;
    /** Persönlicher Vorgang: kein Kontakt-/Risiko-/Vertragsbezug möglich. */
    persoenlich?: boolean;
    /**
     * Beschriftung des Aktionsknopfs; ohne Vorgabe aus den Chips abgeleitet
     * („ändern", sobald einer steht, sonst „hinzufügen"). Freier Text, weil der
     * Anlege-Kasten seit PO 05.09.2026 ausdrücklich „Kontakt, Risiko, Vertrag
     * hinzufügen" schreibt — dort steht der Knopf allein in seiner Zeile und
     * muss sagen, worum es überhaupt geht.
     */
    actionLabel?: string;
    /**
     * Statt des beschrifteten Knopfs ein reines „+" (PO 06.09.2026: „Zuweisung
     * — Buttons anzeigen, also was zugewiesen worden ist, und dann ein + für
     * Hinzufügen"). Im To-do-Kopf steht die Zeile neben Fristen und Erledigen;
     * ein weiteres Wort hätte dort mit den Chips um die Aufmerksamkeit
     * konkurriert. Die Beschriftung geht nicht verloren — sie wandert nach
     * `title` und `aria-label`, der Knopf bleibt also benennbar und für
     * Screenreader unverändert eindeutig.
     */
    alsPlus?: boolean;
    /**
     * Trägt der Kunden-Chip zusätzlich das Schild „Kunde"? (Mockup vom
     * 13.09.2026, `.kunde .tag`.)
     *
     * Nur V4 setzt das: dort ist der Chip mit To-dos die einzige Zeile des
     * Kopfes, und ohne Schild steht der Firmenname nackt in der Karte — er
     * könnte ebenso ein Versicherer oder ein Dienstleister sein. In den
     * übrigen Ansichten steht er in einer Reihe mit Risiko und Vertrag, die
     * ihr Sortenwort schon tragen; dort sagte das Schild nichts Neues.
     *
     * PROP statt Umbau, weil `ZuweisungFeld` auch vom Anlege-Kasten
     * mitbenutzt wird. Ein String ersetzt den Schildtext — V4 gibt den
     * Kontaktstatus mit (z. B. „Interessent", T-332). Die Optik steht in `vorgangV4.css` unter
     * `.vg4-z1` — der einzigen Stelle, an der das Schild erscheint.
     */
    kundenSchild?: boolean | string;
};

type SlotKind = "kontakt" | "risiko" | "vertrag";

const SORTE_LABEL: Record<SlotKind, string> = {
    kontakt: "Kunde",
    risiko: "Risiko",
    vertrag: "Vertrag",
};

export const ZuweisungFeld: React.FC<Props> = ({
    slots,
    canEdit,
    onApply,
    persoenlich = false,
    actionLabel,
    alsPlus = false,
    kundenSchild = false,
}) => {
    const navigate = useNavigate();
    const [dialog, setDialog] = useState<SlotKind | null>(null);

    const apply = (result: CascadeResult): void => {
        // Leerer Patch = unveränderte Auswahl → kein PUT.
        if (!Object.keys(result.patch).length) return;
        void onApply(result);
    };

    const pickKontakt = (kontakt: KontaktRef) => {
        setDialog(null);
        apply(kontakteGewaehlt(slots, [kontakt]));
    };

    const pickKontakte = (kontakte: KontaktRef[]) => {
        setDialog(null);
        apply(kontakteGewaehlt(slots, kontakte));
    };

    const pickRisiken = async (risiken: RisikoRef[]) => {
        setDialog(null);
        apply(await risikenGewaehlt(slots, risiken));
    };

    const pickVertraege = async (vertraege: VertragRef[]) => {
        setDialog(null);
        apply(await vertraegeGewaehlt(slots, vertraege));
    };

    if (persoenlich) {
        return <span className="vg3-leer">Persönlicher Vorgang — ohne Kunden-, Risiko- oder Vertragsbezug.</span>;
    }

    /**
     * Ein Wert-Chip: Sorte klein, Bezeichnung groß; Klick springt zum Datensatz.
     *
     * Beim KUNDEN steht statt des Wortes „Kunde" das Firma-/Person-Icon
     * (PO 09.09.2026: „wichtig Firma/Privat"). Ob man mit einem Unternehmen
     * oder einem Menschen zu tun hat, entscheidet Ton und Weg — das Wort
     * „Kunde" sagte darüber nichts und wiederholte nur, was „Vorgang zu"
     * davor schon ankündigt. Damit die Angabe nicht allein an der Form hängt,
     * nennt der Titel des Chips sie im Klartext.
     */
    const chip = (kind: SlotKind, label: string, id: number, weitere: number) => {
        const typ = kind === "kontakt" ? (slots.kontakt?.typ === "Firma" ? "Firma" : "Person") : null;
        return (
            <button
                key={kind}
                type="button"
                // Die Sorte steht zusätzlich als Klasse am Chip, damit eine
                // Ansicht einen einzelnen Slot hervorheben kann (V4 setzt den
                // Kundennamen größer). `vg3-slot` bleibt unverändert die
                // tragende Klasse — die Sortenklasse ist rein additiv.
                className={`vg3-slot vg3-slot-${kind}`}
                title={typ ? `${typ === "Firma" ? "Firma" : "Privat"} · ${label} öffnen` : `${label} öffnen`}
                onClick={() =>
                    navigate(
                        kind === "kontakt" ? `/contacts/${id}` : kind === "risiko" ? `/risks/${id}` : `/contracts/${id}`,
                    )
                }
            >
                {typ ? (
                    <span className="vg3-slot-typ" aria-hidden>
                        {typ === "Firma" ? FirmaIcon : PersonIcon}
                    </span>
                ) : (
                    <span className="vg3-slot-kind">{SORTE_LABEL[kind]}</span>
                )}
                <span className="vg3-slot-t">{label}</span>
                {/* Das Schild „Kunde" HINTER dem Namen (Mockup 13.09.2026):
                    zuerst steht da, wer es ist, dann was er für uns ist.
                    Nur auf Wunsch der Ansicht — siehe `kundenSchild`. */}
                {kundenSchild && kind === "kontakt" && <span className="vg3-slot-schild">{typeof kundenSchild === "string" ? kundenSchild : "Kunde"}</span>}
                {weitere > 0 && <span className="vg3-badge">+{weitere}</span>}
            </button>
        );
    };

    /**
     * Die Kontaktverantwortlichkeit VOR dem Kunden (PO 07.09.2026: „die
     * Verantwortlichkeit bei einem Vorgang vor Kontakt/Kunde mit anzeigen").
     * Wer den Kunden betreut, entscheidet, an wen man sich wendet — in der
     * Vorgangstabelle steht die Angabe längst, in der Detailansicht fehlte sie.
     *
     * Dieselbe Darstellung wie in Tabelle und Kontaktakte (`FoBoBadge`:
     * BO-Team-Pille, FO-Kürzelkreis). Ohne gepflegte Verantwortlichkeit bleibt
     * die Stelle leer statt einen Strich zu zeigen — die Zeile weist Bezüge
     * aus, sie ist kein Pflegeformular.
     */
    const kv =
        slots.kontakt && (slots.kontakt.boTeam?.name || slots.kontakt.foUser) ? (
            <span
                key="kv"
                className="vg3-kv"
                title="Verantwortlich für den Kunden (BO-Team | FO)"
            >
                <FoBoBadge boTeam={slots.kontakt.boTeam} foUser={slots.kontakt.foUser} muted="var(--ink-3)" />
            </span>
        ) : null;

    const chips = [
        slots.kontakt
            ? chip("kontakt", kontaktLabel(slots.kontakt), slots.kontakt.id, Math.max(0, (slots.kontakte?.length ?? 1) - 1))
            : null,
        // Die Verantwortlichkeit steht seit PO 09.09.2026 HINTER dem Kunden:
        // die Zeichnung liest „Kontaktname (BO: CLL | FO: X)", und so gehört
        // sie auch gesprochen — erst wer, dann wer dafür zuständig ist.
        kv,
        slots.risiken.length
            ? chip("risiko", risikoLabel(slots.risiken[0]), slots.risiken[0].id, slots.risiken.length - 1)
            : null,
        slots.vertraege.length
            ? chip("vertrag", vertragLabel(slots.vertraege[0]), slots.vertraege[0].id, slots.vertraege.length - 1)
            : null,
    ].filter(Boolean);

    return (
        <>
            {chips}

            {canEdit && (
                <RdPopover
                    width={210}
                    dialogLabel="Zuweisung ändern"
                    trigger={({ toggle, ref }) => {
                        const wort = actionLabel ?? (chips.length > 0 ? "ändern" : "hinzufügen");
                        // Der Name für Screenreader nennt IMMER den Gegenstand:
                        // ein Knopf, der nur „+" oder „ändern" heißt, sagt in
                        // einer Zeile mit Fristen und Erledigen nicht, was er
                        // ändert.
                        const name = wort.toLowerCase().startsWith("zuweisung") ? wort : `Zuweisung ${wort}`;
                        return alsPlus ? (
                            <button
                                ref={ref}
                                type="button"
                                className="vg3-ibtn vg3-zuw-plus"
                                title={name}
                                aria-label={name}
                                onClick={toggle}
                            >
                                <IcoPlus size={14} />
                            </button>
                        ) : (
                            <button ref={ref} type="button" className="vg3-tbtn" onClick={toggle}>
                                {wort}
                            </button>
                        );
                    }}
                >
                    {(close) => (
                        // Portal an `body` — außerhalb des `.kvp-rd`-Wrappers der
                        // Seite; ohne eigene Hülle griffen weder Regeln noch Tokens.
                        <div className="kvp-rd vg3-menu">
                            {(["kontakt", "risiko", "vertrag"] as SlotKind[]).map((k) => (
                                <button
                                    key={k}
                                    type="button"
                                    className="vg3-menu-item"
                                    onClick={() => {
                                        close();
                                        setDialog(k);
                                    }}
                                >
                                    {SORTE_LABEL[k]}
                                </button>
                            ))}
                        </div>
                    )}
                </RdPopover>
            )}

            {dialog && (
                <LinkSelectionDialog
                    kind={dialog}
                    slots={slots}
                    onClose={() => setDialog(null)}
                    onKontakt={pickKontakt}
                    onKontakte={pickKontakte}
                    onRisiken={(r) => void pickRisiken(r)}
                    onVertraege={(v) => void pickVertraege(v)}
                />
            )}
        </>
    );
};
